import React from 'react';
import {Box, SxProps, Theme} from '@mui/material';
import {keyframes, styled} from '@mui/material/styles';

/** Pop-in on state change — callers remount the chip (key on `done`) so the
 *  todo→done swap animates like the prototype's @keyframes chipPop. */
const chipPop = keyframes`
  0% { transform: scale(.4); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;

const Chip = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'done' && prop !== 'chipSize' && prop !== 'borderColor' && prop !== 'layer',
})<{ done: boolean; chipSize: number; borderColor: string; layer?: boolean }>(({done, chipSize, borderColor, layer}) => ({
    width: chipSize,
    height: chipSize,
    borderRadius: '50%',
    border: `${chipSize < 20 ? 1.5 : 2}px solid ${borderColor}`,
    // A USER LAYER is not a checklist item to complete — it is content the user
    // added — so it keeps its number and takes a secondary colour instead of
    // going through the todo→done swap. Otherwise every layer showed a green ✓
    // the moment it existed, which reads as "one more field you filled".
    background: layer ? '#8b5cf6' : done ? '#17c47f' : '#2ea6e6',
    color: layer ? '#f5f3ff' : done ? '#04241a' : '#04202f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: Math.max(9, Math.round(chipSize * 0.54)),
    lineHeight: 1,
    flexShrink: 0,
    animation: `${chipPop} .3s ease`,
}));

interface NumberChipProps {
    /** 1-based field number — matches the checklist and the canvas marker. */
    number: number;
    /** Filled state: blue number → green ✓. Ignored for a layer chip. */
    done: boolean;
    /** This chip marks a USER LAYER, not a template field: purple, always the
     *  number. */
    layer?: boolean;
    chipSize?: number;
    /** Border color, set to the surface behind the chip so it pops (page bg on
     *  the canvas, card bg in the checklist). */
    borderColor?: string;
    sx?: SxProps<Theme>;
    /** Physical positioning (left/top). Use this instead of sx for coords: the
     *  RTL emotion cache flips `left`→`right` in sx; inline styles stay put. */
    style?: React.CSSProperties;
}

/** The shared field marker chip used both on the canvas overlay and the
 *  checklist cards, so the two always show the same number/state. The ✓ is a
 *  typographic character (not an emoji), per the app's icon rules. */
const NumberChip: React.FC<NumberChipProps> = ({
                                                   number,
                                                   done,
                                                   layer,
                                                   chipSize = 26,
                                                   borderColor = '#071726',
                                                   sx,
                                                   style,
                                               }) => (
    <Chip done={done} layer={layer} chipSize={chipSize} borderColor={borderColor}
          data-testid={layer ? 'layer-chip' : 'field-chip'} sx={sx} style={style}>
        {layer || !done ? number : '✓'}
    </Chip>
);

export default React.memo(NumberChip);
